import { useCallback, useEffect, useRef, useState } from "react";
import {
  ConsumerClient,
  MessagesClient,
  type MessageView,
} from "../api/generated";
import { useSession } from "../auth/SessionContext";
import { Alert, Chip, Empty, Panel, SeverityChip, describeError, useAsync, when } from "../components/ui";

const consumer = new ConsumerClient("");
const messages = new MessagesClient("");

const WAIT_SECONDS = 30;
const RETRY_AFTER_ERROR_MS = 5000;

/**
 * Console module: <b>Inbox</b>.
 *
 * A person's own messages. This is the console half of what an agent will do through MCP, and it
 * completes person-to-person messaging.
 *
 * The poll follows the §6.6 client contract exactly, because the contract is not advice: one full
 * load on arrival, then incremental polls carrying the previous watermark; incremental polls only
 * ever <em>add</em>, so expiry is the client dropping a message when its own clock passes
 * `expires_at`, and a periodic full reload reconciles everything else. Ack-required messages are
 * the exception the server enforces — they keep arriving past expiry until acknowledged.
 */
export function InboxPage() {
  const { session } = useSession();
  const hasParty = !!session?.scope?.party;

  const [items, setItems] = useState<MessageView[]>([]);

  // Two error slots, not one. The poll runs on its own every few seconds and used to clear the
  // single slot on every success, which erased whatever a person had just been told: click Reply,
  // get refused, and the refusal disappears before it can be read. A poll now clears only its own
  // failure, and an action clears only its own.
  const [pollError, setPollError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const error = actionError ?? pollError;
  const [loading, setLoading] = useState(hasParty);
  const [waiting, setWaiting] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [generation, setGeneration] = useState(0);

  const seen = useRef(new Map<string, MessageView>());

  // A ticking clock, so "overdue" and expiry become true without waiting for a poll.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!hasParty) return;

    let cancelled = false;
    let since: number | undefined;

    // The first call is a full inbox load — no `since` — and every call after it is incremental,
    // held open until a subscribed stream head moves past the watermark.
    async function loop() {
      while (!cancelled) {
        try {
          const response = await consumer.consumer_Inbox(
            since,
            since === undefined ? 0 : WAIT_SECONDS,
            100,
          );
          if (cancelled) return;

          if (since === undefined) seen.current = new Map();
          for (const message of response.messages ?? []) {
            seen.current.set(message.id!, message);
          }

          since = response.watermark;
          setItems([...seen.current.values()]);
          setPollError(null);
          setLoading(false);
          setWaiting(true);
        } catch (e) {
          if (cancelled) return;
          setPollError(describeError(e));
          setLoading(false);
          setWaiting(false);
          await new Promise((resolve) => window.setTimeout(resolve, RETRY_AFTER_ERROR_MS));
        }
      }
    }

    void loop();
    return () => { cancelled = true; };
  }, [hasParty, generation]);

  const patch = useCallback((id: string, change: Partial<MessageView> | null) => {
    const existing = seen.current.get(id);

    // A reload may have replaced the map while the action was in flight. Nothing to patch then —
    // the next poll carries the server's answer anyway.
    if (change === null) seen.current.delete(id);
    else if (existing) seen.current.set(id, { ...existing, ...change });

    setItems([...seen.current.values()]);
  }, []);

  async function act(id: string, action: () => Promise<unknown>, change: Partial<MessageView> | null) {
    setActionError(null);
    try {
      await action();
      patch(id, change);
    } catch (e) {
      setActionError(describeError(e));
    }
  }

  if (!hasParty) {
    return (
      <>
        <div className="page-head"><div><h1>Inbox</h1></div></div>
        <Panel title="No inbox">
          <p className="muted" style={{ maxWidth: "70ch" }}>
            Your account is not linked to a party, so it cannot receive messages.
          </p>
          <p className="muted" style={{ maxWidth: "70ch" }}>
            An administrator can link one under Users &amp; access — open the account and set a
            party under Inbox.
          </p>
        </Panel>
      </>
    );
  }

  const visible = items
    .filter((m) => !isGone(m, now))
    .sort(compareForDisplay);

  const unread = visible.filter((m) => !m.myState || m.myState === "DELIVERED").length;
  const needsAck = visible.filter((m) => m.requiresAcknowledgement && m.myState !== "ACKNOWLEDGED").length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Inbox</h1>
          <p className="sub">
            Reading as <span className="mono">{session?.scope?.party?.key}</span>
            {waiting && <span className="muted"> · listening for new messages</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {unread > 0 && <Chip kind="on">{unread} unread</Chip>}
          {needsAck > 0 && <Chip kind="warn">{needsAck} to acknowledge</Chip>}
          <button className="ghost small" onClick={() => { setLoading(true); setGeneration((g) => g + 1); }}>
            Reload
          </button>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {loading ? (
        <Panel><Empty>Loading…</Empty></Panel>
      ) : visible.length === 0 ? (
        <Panel>
          <Empty>No messages. New ones appear here automatically.</Empty>
        </Panel>
      ) : (
        visible.map((m) => (
          <MessageCard
            key={m.id}
            message={m}
            now={now}
            expanded={open === m.id}
            onToggle={() => setOpen(open === m.id ? null : m.id!)}
            onRead={() => act(m.id!, () => messages.messages_Receipt(m.id!, { state: "READ" }), { myState: "READ" })}
            onAcknowledge={() =>
              act(m.id!, () => messages.messages_Receipt(m.id!, { state: "ACKNOWLEDGED" }), { myState: "ACKNOWLEDGED" })}
            onDismiss={() => act(m.id!, () => messages.messages_Dismiss(m.id!), null)}
            onError={setActionError}
          />
        ))
      )}
    </>
  );
}

function MessageCard({ message, now, expanded, onToggle, onRead, onAcknowledge, onDismiss, onError }: {
  message: MessageView;
  now: number;
  expanded: boolean;
  onToggle: () => void;
  onRead: () => void;
  onAcknowledge: () => void;
  onDismiss: () => void;
  onError: (message: string | null) => void;
}) {
  const unread = !message.myState || message.myState === "DELIVERED";
  const acknowledged = message.myState === "ACKNOWLEDGED";
  const overdue = !!message.dueAt && new Date(message.dueAt).getTime() < now;

  return (
    <Panel>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ marginBottom: 4 }}>
            {unread && <span className="chip on" style={{ marginRight: 6 }}>new</span>}
            {message.presentation?.title || message.subject}
          </h3>
          <div className="muted" style={{ fontSize: 12 }}>
            {message.producer?.name} · {when(message.publishedAt)} ·{" "}
            <span className="mono">{message.stream}/{message.subject}</span>
          </div>
        </div>
        <div style={{ whiteSpace: "nowrap" }}>
          <SeverityChip severity={message.severity} />
          {(message.flags ?? []).map((f) => <Chip key={f}>{f}</Chip>)}
          {overdue && <Chip kind="crit">overdue</Chip>}
          {message.requiresAcknowledgement && !acknowledged && <Chip kind="warn">acknowledge</Chip>}
          {acknowledged && <Chip kind="ok">acknowledged</Chip>}
        </div>
      </div>

      {message.presentation?.body && (
        <p style={{ whiteSpace: "pre-wrap", marginBottom: 8 }}>{message.presentation.body}</p>
      )}

      {(message.presentation?.actions ?? []).length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {message.presentation!.actions!.map((a) => (
            <a key={a.url} href={a.url} target="_blank" rel="noreferrer" style={{ marginRight: 12 }}>
              {a.label} ↗
            </a>
          ))}
        </div>
      )}

      {(message.dueAt || message.expiresAt) && (
        <p className="muted" style={{ fontSize: 12 }}>
          {message.dueAt && <>Due {when(message.dueAt)}. </>}
          {message.expiresAt && <>Disappears {when(message.expiresAt)}.</>}
        </p>
      )}

      <div className="row" style={{ marginTop: 4 }}>
        {unread && <button className="ghost small" onClick={onRead}>Mark as read</button>}
        {message.requiresAcknowledgement && !acknowledged && (
          <button className="small" onClick={onAcknowledge}>Acknowledge</button>
        )}
        {message.allowsReply && (
          <button className="ghost small" onClick={onToggle}>{expanded ? "Close" : "Reply"}</button>
        )}
        {(!message.requiresAcknowledgement || acknowledged) && (
          <button className="ghost small" onClick={onDismiss}>Dismiss</button>
        )}
        {message.requiresAcknowledgement && !acknowledged && (
          <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>
            Cannot be dismissed until acknowledged.
          </span>
        )}
      </div>

      {expanded && <Thread message={message} onError={onError} />}
    </Panel>
  );
}

function Thread({ message, onError }: { message: MessageView; onError: (message: string | null) => void }) {
  const { data: thread, reload } = useAsync(() => messages.messages_Thread(message.id!), [message.id]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    onError(null);
    try {
      await messages.messages_Reply(message.id!, { presentation: { body, actions: [] } });
      setBody("");
      await reload();
    } catch (e) {
      onError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
      {thread && thread.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          {thread.map((m) => (
            <div key={m.id} style={{ marginBottom: 8 }}>
              <div className="muted" style={{ fontSize: 12 }}>
                {m.producer?.name} · {when(m.publishedAt)}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{m.presentation?.body || m.presentation?.title}</div>
            </div>
          ))}
        </div>
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply"
      />
      <div className="row" style={{ marginTop: 8 }}>
        <button disabled={busy || !body.trim()} onClick={send}>Send reply</button>
        <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>
          Replies go only to the sender.
        </span>
      </div>
    </div>
  );
}

/**
 * Removal by expiry is the client's job: every message carries `expires_at`, and an incremental
 * poll only ever adds. The exception is the server's — an ack-required message keeps arriving past
 * expiry until it is acknowledged, and it must keep showing here too.
 */
function isGone(message: MessageView, now: number): boolean {
  if (!message.expiresAt) return false;
  if (new Date(message.expiresAt).getTime() > now) return false;

  return !(message.requiresAcknowledgement && message.myState !== "ACKNOWLEDGED");
}

/** Pinned, then severity, then newest — the same order the server applies to a page (§6). */
function compareForDisplay(a: MessageView, b: MessageView): number {
  const rank = { critical: 2, warning: 1, info: 0 } as Record<string, number>;
  const now = Date.now();

  const pinned = (m: MessageView) => (m.pinnedUntil && new Date(m.pinnedUntil).getTime() > now ? 1 : 0);

  return pinned(b) - pinned(a)
    || (rank[b.severity ?? "info"] ?? 0) - (rank[a.severity ?? "info"] ?? 0)
    || (b.sequence ?? 0) - (a.sequence ?? 0);
}
